import * as styles from "./MapView.css";

import { clsx } from "clsx";
import { useAtomValue } from "jotai";
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import { Skeleton } from "@frontend/components/atoms/Skeleton";
import { resolvedThemeAtom } from "@frontend/stores";
import { sizeToCss } from "@frontend/styles";
import { useLingui } from "@lingui/react/macro";
import {
  APILoadingStatus,
  ColorScheme,
  Map as GoogleMap,
  useApiLoadingStatus,
  useMap,
} from "@vis.gl/react-google-maps";

import { MapProvider } from "./MapProvider";
import {
  MapContentContext,
  type MapContentRegistry,
  MapPaletteContext,
} from "./MapView.context";
import type { MapBounds, MapLatLng, MapViewProps } from "./MapView.types";
import {
  DEFAULT_CENTER,
  DEFAULT_HEIGHT,
  DEFAULT_MAP_ID,
  DEFAULT_ZOOM,
  FIT_PADDING,
  FIT_SINGLE_ZOOM,
  HTML_DATASET_MAP_ID,
} from "./constants";
import { boundsEqual, isPointBounds, unionOf } from "./mapBounds";
import { useMapPalette } from "./useMapPalette";

function readMapId(): string {
  return (
    document.documentElement.dataset[HTML_DATASET_MAP_ID] || DEFAULT_MAP_ID
  );
}

export function MapView({
  center,
  zoom = DEFAULT_ZOOM,
  fit = center ? "none" : "content",
  height = DEFAULT_HEIGHT,
  mapId,
  interactive = true,
  controls = interactive,
  label,
  onClick,
  className,
  style,
  children,
  ...props
}: MapViewProps): JSX.Element {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const palette = useMapPalette(root);

  const [content, setContent] = useState<ReadonlyMap<string, MapBounds>>(
    new Map(),
  );

  const registry = useMemo<MapContentRegistry>(
    () => ({
      register: (id, bounds) =>
        setContent((previous) => {
          if (boundsEqual(previous.get(id) ?? null, bounds)) {
            return previous;
          }

          return new Map(previous).set(id, bounds);
        }),
      unregister: (id) =>
        setContent((previous) => {
          if (!previous.has(id)) {
            return previous;
          }

          const next = new Map(previous);

          next.delete(id);

          return next;
        }),
    }),
    [],
  );

  const contentBounds = useMemo(
    () => unionOf([...content.values()]),
    [content],
  );

  const target: MapBounds | null =
    fit === "none" ? null : fit === "content" ? contentBounds : fit;

  return (
    <div
      ref={setRoot}
      data-slot="map-view"
      role="region"
      aria-label={label}
      className={clsx(styles.root, className)}
      style={{ height: sizeToCss(height), ...style }}
      {...props}
    >
      <MapProvider>
        <MapPaletteContext.Provider value={palette}>
          <MapContentContext.Provider value={registry}>
            <MapViewCanvas
              center={center}
              zoom={zoom}
              target={target}
              mapId={mapId ?? readMapId()}
              interactive={interactive}
              controls={controls}
              onClick={onClick}
            >
              {children}
            </MapViewCanvas>
          </MapContentContext.Provider>
        </MapPaletteContext.Provider>
      </MapProvider>
    </div>
  );
}

type MapViewCanvasProps = {
  center: MapLatLng | undefined;
  zoom: number;
  target: MapBounds | null;
  mapId: string;
  interactive: boolean;
  controls: boolean;
  onClick: ((position: MapLatLng) => void) | undefined;
  children: ReactNode;
};

function MapViewCanvas({
  center,
  zoom,
  target,
  mapId,
  interactive,
  controls,
  onClick,
  children,
}: MapViewCanvasProps): JSX.Element {
  const { t } = useLingui();
  const status = useApiLoadingStatus();
  const theme = useAtomValue(resolvedThemeAtom);

  if (
    status === APILoadingStatus.FAILED ||
    status === APILoadingStatus.AUTH_FAILURE
  ) {
    return (
      <div data-slot="map-view-placeholder" className={styles.placeholder}>
        <Icon name="MapPinOff" size="lg" />
        <span>{t`The map could not be loaded.`}</span>
      </div>
    );
  }

  if (status !== APILoadingStatus.LOADED) {
    return <Skeleton className={styles.skeleton} radius="none" />;
  }

  return (
    <GoogleMap
      className={styles.canvas}
      mapId={mapId}
      colorScheme={theme === "dark" ? ColorScheme.DARK : ColorScheme.LIGHT}
      defaultCenter={center ?? DEFAULT_CENTER}
      defaultZoom={zoom}
      disableDefaultUI
      zoomControl={controls}
      gestureHandling={interactive ? "auto" : "none"}
      keyboardShortcuts={interactive}
      clickableIcons={false}
      onClick={(event) => {
        if (onClick && event.detail.latLng) {
          onClick(event.detail.latLng);
        }
      }}
    >
      <MapCamera center={center} zoom={zoom} target={target} />
      {children}
    </GoogleMap>
  );
}

type MapCameraProps = {
  center: MapLatLng | undefined;
  zoom: number;
  target: MapBounds | null;
};

// Moves the camera when the props ask for it, and leaves it alone otherwise:
// the map stays uncontrolled so the user can pan and zoom freely.
function MapCamera({ center, zoom, target }: MapCameraProps): null {
  const map = useMap();
  const fitted = useRef<MapBounds | null>(null);

  const fitTo = useCallback(
    (bounds: MapBounds) => {
      if (!map) {
        return;
      }

      if (isPointBounds(bounds)) {
        map.setCenter({ lat: bounds.north, lng: bounds.east });
        map.setZoom(FIT_SINGLE_ZOOM);

        return;
      }

      map.fitBounds(bounds, FIT_PADDING);
    },
    [map],
  );

  useEffect(() => {
    if (!map || !target || boundsEqual(fitted.current, target)) {
      return;
    }

    fitted.current = target;
    fitTo(target);
  }, [map, target, fitTo]);

  const lat = center?.lat;
  const lng = center?.lng;
  const appliedCenter = useRef(center);

  useEffect(() => {
    const previous = appliedCenter.current;

    if (lat === undefined || lng === undefined) {
      appliedCenter.current = undefined;

      return;
    }

    appliedCenter.current = { lat, lng };

    if (map && (previous?.lat !== lat || previous?.lng !== lng)) {
      map.panTo({ lat, lng });
    }
  }, [map, lat, lng]);

  const appliedZoom = useRef(zoom);

  useEffect(() => {
    if (map && appliedZoom.current !== zoom) {
      appliedZoom.current = zoom;
      map.setZoom(zoom);
    }
  }, [map, zoom]);

  return null;
}
